import { afterNextRender, Directive, ElementRef, inject } from '@angular/core';

import { COOKIE_SETTINGS_HREF, CookieConsentService } from '../../services/cookie-consent/cookie-consent.service';

const COOKIE_SETTINGS_LINK = `a[href="${COOKIE_SETTINGS_HREF}"]`;

/**
 * Turns every `<a href="#cookie-settings">` inside the host into the way back into the consent kit's
 * Cookie settings dialog (AT-2748).
 *
 * The page footer's links are the design system's `at-footer`, which only draws `FooterLink`s (`routerLink`
 * or `href`) and has no click output. The kit's own dialog tells people the link is "beside our privacy
 * policy", so it has to be one of those `FooterLink`s, drawn by the footer like its neighbours, separator
 * and all; this directive, on the footer, answers the click.
 *
 * A plain click keeps the browser off the link: behind `<base href="/">` the bare fragment would leave the
 * page for `/#cookie-settings`. That is exactly where the browser's own new-tab gestures should go, so they
 * pass through (a middle-click is `auxclick` and never reaches here), and the new tab opens the dialog
 * (`CookieConsentService.openIfLinked`).
 */
@Directive({
  selector: '[appCookieSettingsLinks]',
  host: { '(click)': 'onClick($event)' },
})
export class CookieSettingsLinksDirective {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly consent = inject(CookieConsentService);

  constructor() {
    // The footer draws its links in its own template, so they exist only once it has rendered.
    afterNextRender(() => {
      this.host.querySelectorAll(COOKIE_SETTINGS_LINK).forEach((link) => link.setAttribute('aria-haspopup', 'dialog'));
    });
  }

  protected onClick(event: MouseEvent): void {
    const link = event.target instanceof Element ? event.target.closest(COOKIE_SETTINGS_LINK) : null;
    if (!link || !this.host.contains(link) || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }

    event.preventDefault();
    this.consent.openSettings();
  }
}
